import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const MoodStats = ({ chartData }) => {
    const { t } = useTranslation();

    // Move useMemo to component level instead of inside a function
    const moodConfig = useMemo(() => ({
        1: {
            text: t('moodChart.levels.veryLow'),
            color: '#EF4444',
            emoji: '😢',
            description: t('moodChart.descriptions.veryLow'),
            tip: t('moodChart.tips.veryLow')
        },
        2: {
            text: t('moodChart.levels.low'),
            color: '#F59E0B',
            emoji: '😕',
            description: t('moodChart.descriptions.low'),
            tip: t('moodChart.tips.low')
        },
        3: {
            text: t('moodChart.levels.neutral'),
            color: '#10B981',
            emoji: '😐',
            description: t('moodChart.descriptions.neutral'),
            tip: t('moodChart.tips.neutral')
        },
        4: {
            text: t('moodChart.levels.good'),
            color: '#3B82F6',
            emoji: '😊',
            description: t('moodChart.descriptions.good'),
            tip: t('moodChart.tips.good')
        },
        5: {
            text: t('moodChart.levels.excellent'),
            color: '#6366F1',
            emoji: '😄',
            description: t('moodChart.descriptions.excellent'),
            tip: t('moodChart.tips.excellent')
        }
    }), [t]);

    // Safety check for when data is not provided
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        return null;
    }

    // Filter valid moods
    const validMoods = chartData.filter(entry => entry.mood !== null && entry.mood !== undefined);

    // Return null if no valid moods exist
    if (validMoods.length === 0) {
        return null;
    }

    // Calculate statistics
    const avgMood = validMoods.length > 0
        ? (validMoods.reduce((sum, entry) => sum + entry.mood, 0) / validMoods.length).toFixed(1)
        : null;

    const bestDay = validMoods.length > 0
        ? validMoods.reduce((best, entry) => entry.mood > best.mood ? entry : best, validMoods[0])
        : null;

    const worstDay = validMoods.length > 0
        ? validMoods.reduce((worst, entry) => entry.mood < worst.mood ? entry : worst, validMoods[0])
        : null;

    // Find predominant mood
    const moodCounts = validMoods.reduce((counts, entry) => {
        counts[entry.mood] = (counts[entry.mood] || 0) + 1;
        return counts;
    }, {});

    let predominantMood = null;
    let maxCount = 0;
    Object.entries(moodCounts).forEach(([mood, count]) => {
        if (count > maxCount) {
            maxCount = count;
            predominantMood = Number(mood);
        }
    });

    // Calculate trend (improving, worsening, or stable)
    let trend = "stable";
    let trendIcon = "→";
    let trendColor = "#6B7280"; // grey

    if (validMoods.length >= 3) {
        const recentMoods = [...validMoods].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
        const oldestMood = recentMoods[2].mood;
        const newestMood = recentMoods[0].mood;

        if (newestMood > oldestMood) {
            trend = "improving";
            trendIcon = "↗️";
            trendColor = "#10B981"; // green
        } else if (newestMood < oldestMood) {
            trend = "worsening";
            trendIcon = "↘️";
            trendColor = "#EF4444"; // red
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4"
        >
            {avgMood !== null && (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('moodChart.average')}</p>
                    <div className="flex items-center mt-1">
                        <span className="text-lg sm:text-xl font-bold text-indigo-500">{avgMood}</span>
                        <span className="text-base sm:text-lg ml-2">{moodConfig[Math.round(Number(avgMood))]?.emoji || '❓'}</span>
                    </div>
                </div>
            )}

            {predominantMood !== null && (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('moodChart.frequentMood')}</p>
                    <div className="flex items-center mt-1">
                        <span className="text-base sm:text-lg">{moodConfig[predominantMood]?.emoji || '❓'}</span>
                        <span className="text-xs sm:text-sm font-medium ml-2" style={{ color: moodConfig[predominantMood]?.color || '#6B7280' }}>
                            {moodConfig[predominantMood]?.text || t('moodChart.unknown')}
                        </span>
                    </div>
                </div>
            )}

            {validMoods.length >= 3 && (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('moodChart.trend')}</p>
                    <div className="flex items-center mt-1">
                        <span className="text-lg">{trendIcon}</span>
                        <span className="text-xs sm:text-sm ml-2" style={{ color: trendColor }}>
                            {t(`moodChart.trends.${trend}`)}
                        </span>
                    </div>
                </div>
            )}

            {validMoods.length > 0 && (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('moodChart.records')}</p>
                    <p className="text-lg sm:text-xl font-bold text-indigo-500 mt-1">{validMoods.length}</p>
                </div>
            )}
        </motion.div>
    );
};

export default MoodStats;